<template>
  <a-modal
      v-model:visible="visible"
      title="导入项目工单"
      :on-before-ok="handleOk"
      cancel-text="关闭"
      @cancel="handleCancel"
      width="600px"
      :mask-closable="false"
      :ok-loading="loading"
      ok-text="开始导入"
      draggable
  >
    <div class="import-content">
      <!-- 模板下载区域 -->
      <a-alert 
        type="info" 
        style="margin-bottom: 20px;"
        :show-icon="true"
      >
        <template #title>
          导入说明
        </template>
        <div>
          <p>1. 请先下载模板文件，按照模板格式填写数据</p>
          <p>2. 支持的文件格式：Excel (.xlsx, .xls)</p>
          <p>3. 文件大小不超过10MB</p>
          <p>4. 必填字段：工单编码、工单类型、任务类型、所属门店、联系人、联系电话、预计工时(分钟)
          </p>
        </div>
      </a-alert>

      <!-- 模板下载按钮 -->
      <div class="template-download" style="margin-bottom: 20px;">
        <a-space>
          <a-button 
            type="outline" 
            @click="downloadLocalTemplate"
          >
            <template #icon>
              <icon-download />
            </template>
            下载本地模板
          </a-button>
        </a-space>
      </div>

      <!-- 文件上传区域 -->
      <a-upload
          ref="uploadRef"
          v-model:file-list="fileList"
          :auto-upload="false"
          :show-file-list="true"
          :limit="1"
          accept=".xlsx,.xls"
          @change="handleFileChange"
          @remove="handleFileRemove"
          :before-upload="beforeUpload"
      >
        <template #upload-button>
          <div class="upload-area">
            <div class="upload-icon">
              <icon-upload />
            </div>
            <div class="upload-text">
              <p>点击选择文件或拖拽文件到此区域</p>
              <p class="upload-tip">仅支持 .xlsx、.xls 格式的文件</p>
            </div>
          </div>
        </template>
        <template #start-icon />
        <template #retry-icon />
      </a-upload>

      <!-- 导入结果展示 -->
      <div v-if="importResult" class="import-result" style="margin-top: 20px;">
        <a-alert 
          :type="importResult.success ? 'success' : 'error'"
          :show-icon="true"
        >
          <template #title>
            导入{{ importResult.success ? '成功' : '失败' }}
          </template>
          <div>
            <p v-if="importResult.success">
              成功导入 <strong>{{ importResult.successCount }}</strong> 条记录
              <span v-if="importResult.failCount > 0">
                ，失败 <strong>{{ importResult.failCount }}</strong> 条记录
              </span>
            </p>
            <p v-else>{{ importResult.message }}</p>
            
            <!-- 失败记录详情 -->
            <div v-if="importResult.failDetails && importResult.failDetails.length > 0">
              <a-divider style="margin: 10px 0;" />
              <p style="font-weight: bold;">失败记录详情：</p>
              <div style="max-height: 200px; overflow-y: auto;">
                <div 
                  v-for="(fail, index) in importResult.failDetails" 
                  :key="index"
                  style="margin-bottom: 5px; font-size: 12px;"
                >
                  第{{ fail.rowNum }}行：{{ fail.message }}
                </div>
              </div>
            </div>
          </div>
        </a-alert>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { ref, watch } from 'vue'
import { Message } from '@arco-design/web-vue'
import { 
  importWorkOrder
} from '@/apis/work-order-api'
import {
  IconDownload,
  IconUpload
} from '@arco-design/web-vue/es/icon'

// 定义 props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

// 定义 emits
const emit = defineEmits(['update:modelValue', 'success'])

// 响应式数据
const visible = ref(false)
const loading = ref(false)
const uploadRef = ref()
const fileList = ref([])
const importResult = ref(null)

// 监听 modelValue 变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal
  if (newVal) {
    resetState()
  }
})

// 监听 visible 变化
watch(visible, (newVal) => {
  emit('update:modelValue', newVal)
})

// 重置状态
const resetState = () => {
  fileList.value = []
  importResult.value = null
  loading.value = false
}

// 下载本地模板文件
const downloadLocalTemplate = () => {
  const link = document.createElement('a')
  link.href = '/work_order_info_template.xlsx'
  link.download = '项目工单导入模板.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  Message.success('本地模板文件下载成功')
}

// 文件上传前验证
const beforeUpload = (file) => {
  const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                  file.type === 'application/vnd.ms-excel' ||
                  file.name.endsWith('.xlsx') ||
                  file.name.endsWith('.xls')
  
  if (!isExcel) {
    Message.error('只能上传Excel文件格式')
    return false
  }
  
  const isLt10M = file.size / 1024 / 1024 < 10
  if (!isLt10M) {
    Message.error('文件大小不能超过10MB')
    return false
  }
  
  return true
}

// 文件变化处理
const handleFileChange = (fileList) => {
  console.log('文件变化处理:', fileList)
  if (fileList.length > 0) {
    importResult.value = null // 清除之前的导入结果
  }
}

// 文件移除处理
const handleFileRemove = () => {
  importResult.value = null
}

// 处理确认按钮
const handleOk = async (done) => {
  if (fileList.value.length === 0) {
    Message.warning('请先选择要导入的文件')
    done(false)
    return false
  }
  
  loading.value = true
  try {
    const file = fileList.value[0].file || fileList.value[0]
    
    let response = await importWorkOrder(file)
    if (response.code === 0) {
      const data = response.data
      importResult.value = {
        success: true,
        successCount: data.successCount || 0,
        failCount: data.failCount || 0,
        failDetails: data.failDetails || []
      }
      
      Message.success(`导入完成！成功 ${data.successCount || 0} 条，失败 ${data.failCount || 0} 条`)
      
      // 如果有成功导入的记录，通知父组件刷新
      if (data.successCount > 0) {
        emit('success')
      }
      
      return false // 不关闭弹框，让用户看到结果
    } else {
      importResult.value = {
        success: false,
        message: response.message || '导入失败'
      }
      Message.error(response.message || '导入失败')
      return false
    }
  } catch (error) {
    importResult.value = {
      success: false,
      message: '导入过程中发生错误'
    }
    Message.error('导入失败')
    console.error('导入失败:', error)
    return false
  } finally {
    done(false)
    loading.value = false
  }
}

// 处理取消按钮
const handleCancel = () => {
  visible.value = false
}
</script>

<style scoped>
.import-content {
  padding: 10px 0;
}

.upload-area {
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  background-color: #fafafa;
  text-align: center;
  padding: 40px 20px;
  transition: border-color 0.3s, background-color 0.3s;
  cursor: pointer;
}

.upload-area:hover {
  border-color: #1890ff;
  background-color: #f5f5f5;
}

.upload-icon {
  font-size: 48px;
  color: #d9d9d9;
  margin-bottom: 16px;
}

.upload-text p {
  margin: 0;
  font-size: 16px;
  color: #666;
}

.upload-tip {
  font-size: 14px !important;
  color: #999 !important;
  margin-top: 8px !important;
}

.import-result {
  border-radius: 6px;
}

.template-download {
  text-align: center;
  padding: 20px;
  background-color: #f5f5f5;
  border-radius: 6px;
}
</style>